import { useState, useEffect, useCallback } from 'react';
import { api, ApiRequestError } from '../lib/api';

interface GalleryImage {
  id: string;
  file_size: number;
  width: number;
  height: number;
  format: string;
  prompt: string;
  scenario_id: string | null;
  quality_level: string;
  thumbnail_url: string;
  created_at: string;
}

interface Scenario {
  id: string;
  display_name: string;
}

export function Gallery() {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'grid' | 'list'>(() => {
    return (sessionStorage.getItem('gallery_view') as 'grid' | 'list') || 'grid';
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterScenario, setFilterScenario] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [error, setError] = useState('');

  const perPage = 20;

  const fetchImages = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {
        page: String(page),
        per_page: String(perPage),
      };
      if (filterScenario) params.scenario_id = filterScenario;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const res = await api.get<{ items: GalleryImage[]; total: number }>('/images', params);
      setImages(res.data?.items ?? []);
      setTotalPages(Math.ceil((res.data?.total ?? 0) / perPage));
    } catch {
      setError('Failed to load images');
    } finally {
      setLoading(false);
    }
  }, [page, filterScenario, dateFrom, dateTo]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  useEffect(() => {
    api
      .get<{ scenarios: Scenario[] }>('/scenarios')
      .then((res) => setScenarios(res.data?.scenarios ?? []))
      .catch(() => {});
  }, []);

  const handleViewChange = (v: 'grid' | 'list') => {
    setView(v);
    sessionStorage.setItem('gallery_view', v);
  };

  const handleDownload = async (id: string) => {
    try {
      const res = await api.get<{ download_url: string }>(`/images/${id}/download`);
      window.open(res.data.download_url, '_blank');
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.error.message);
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this image?')) return;
    try {
      await api.delete(`/images/${id}`);
      setImages((prev) => prev.filter((img) => img.id !== id));
      setSelectedImage(null);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.error.message);
      }
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Image Gallery</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleViewChange('grid')}
            className={`px-3 py-1.5 rounded-md text-sm min-h-[44px] min-w-[44px] ${
              view === 'grid' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
            }`}
            aria-label="Grid view"
          >
            Grid
          </button>
          <button
            onClick={() => handleViewChange('list')}
            className={`px-3 py-1.5 rounded-md text-sm min-h-[44px] min-w-[44px] ${
              view === 'list' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
            }`}
            aria-label="List view"
          >
            List
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">{error}</div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4">
          <div>
            <label htmlFor="filterScenario" className="block text-xs font-medium text-gray-600 mb-1">
              Scenario
            </label>
            <select
              id="filterScenario"
              value={filterScenario}
              onChange={(e) => {
                setFilterScenario(e.target.value);
                setPage(1);
              }}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm min-h-[44px]"
            >
              <option value="">All</option>
              {scenarios.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.display_name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="dateFrom" className="block text-xs font-medium text-gray-600 mb-1">
              From
            </label>
            <input
              id="dateFrom"
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(1);
              }}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm min-h-[44px]"
            />
          </div>
          <div>
            <label htmlFor="dateTo" className="block text-xs font-medium text-gray-600 mb-1">
              To
            </label>
            <input
              id="dateTo"
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(1);
              }}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm min-h-[44px]"
            />
          </div>
        </div>
      </div>

      {/* Image grid/list */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading images...</div>
      ) : images.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No images found</div>
      ) : view === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((img) => (
            <div
              key={img.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setSelectedImage(img)}
            >
              <img
                src={img.thumbnail_url}
                alt={img.prompt}
                className="w-full h-48 object-cover"
              />
              <div className="p-3">
                <p className="text-xs text-gray-600 line-clamp-2">{img.prompt}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {new Date(img.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-100">
          {images.map((img) => (
            <div
              key={img.id}
              className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50"
              onClick={() => setSelectedImage(img)}
            >
              <img
                src={img.thumbnail_url}
                alt={img.prompt}
                className="w-16 h-16 object-cover rounded"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-700 truncate">{img.prompt}</p>
                <p className="text-xs text-gray-400">
                  {img.width}×{img.height} &middot; {img.quality_level} &middot;{' '}
                  {new Date(img.created_at).toLocaleDateString()}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(img.id);
                  }}
                  className="px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 min-h-[44px] min-w-[44px]"
                >
                  Download
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(img.id);
                  }}
                  className="px-3 py-1.5 text-xs bg-red-50 text-red-600 rounded hover:bg-red-100 min-h-[44px] min-w-[44px]"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm disabled:opacity-50 min-h-[44px]"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm disabled:opacity-50 min-h-[44px]"
          >
            Next
          </button>
        </div>
      )}

      {/* Image Preview Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Image Details</h3>
              <button
                onClick={() => setSelectedImage(null)}
                className="p-2 text-gray-500 hover:text-gray-700 min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label="Close preview"
              >
                ✕
              </button>
            </div>
            <div className="p-4">
              <img
                src={selectedImage.thumbnail_url}
                alt={selectedImage.prompt}
                className="w-full max-h-96 object-contain rounded"
              />
              <div className="mt-4 space-y-2">
                <p className="text-sm text-gray-700">{selectedImage.prompt}</p>
                <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                  <span>
                    {selectedImage.width}×{selectedImage.height}
                  </span>
                  <span>{selectedImage.quality_level}</span>
                  <span>{selectedImage.format}</span>
                  <span>{new Date(selectedImage.created_at).toLocaleString()}</span>
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => handleDownload(selectedImage.id)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 min-h-[44px]"
                >
                  Download
                </button>
                <button
                  onClick={() => handleDelete(selectedImage.id)}
                  className="px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 min-h-[44px]"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
